import {useEffect, useRef } from 'react';
import { Stack } from '@mui/material';
import { abyssColors } from '@/theme/colors';
import { NotesLabel, NotesContainer, notesTextareaStyles, NotesDisplayText } from './styles';

type Props = {
  note: string | undefined | null;
  onNoteChange: (newNote: string) => void;
  isEditing?: boolean;
  onEditingChange?: (isEditing: boolean) => void;
  disabled?: boolean;
  canEdit?: boolean;
};

export const PostInspectionNotesEditor = ({
  note,
  onNoteChange,
  isEditing = false,
  onEditingChange,
  disabled = false,
  canEdit = false,
}: Props): JSX.Element => {

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (!isEditing) return;
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [isEditing]);

  return (
    <Stack gap={1} flex={1}>
      <NotesLabel>General notes</NotesLabel>
      <NotesContainer
        onClick={(): void => {
          if (!isEditing && canEdit && !disabled) onEditingChange?.(true);
        }}
        onKeyDown={(e): void => {
          if (isEditing) return;
          const ev = e as React.KeyboardEvent<HTMLElement>;
          if ((ev.key === 'Enter' || ev.key === ' ') && canEdit && !disabled) {
            ev.preventDefault();
            onEditingChange?.(true);
          }
        }}
        role={!isEditing && canEdit ? 'button' : undefined}
        tabIndex={!isEditing && canEdit ? 0 : -1}
        sx={{
          cursor: !isEditing && canEdit && !disabled ? 'pointer' : 'default',
        }}
      >
        {isEditing ? (
          <textarea
            ref={textareaRef}
            value={note ?? ''}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>): void =>
              onNoteChange(e.target.value)
            }
            onKeyDown={(event: React.KeyboardEvent<HTMLTextAreaElement>): void => {
              if (event.key === 'Escape') {
                event.preventDefault();
                onEditingChange?.(false);
              }
            }}
            onBlur={(): void => {
              onEditingChange?.(false);
            }}
            disabled={disabled}
            placeholder="Enter general note..."
            autoFocus
            style={notesTextareaStyles}
          />
        ) : (
          <NotesDisplayText
            variant="caption"
            sx={{ color: note ? undefined : abyssColors.primary[300] }}
          >
            {note || 'No notes added yet'}
          </NotesDisplayText>
        )}
      </NotesContainer>
    </Stack>
  );
};
